import {
  DurationPickerV2 as DurationPicker,
  FieldError,
  NumberField,
  TextField,
  Typography,
} from '@automata/ui';
import {
  castTimeBreakdownStrings,
  getTimeBreakdownStrings,
  getTotalSeconds,
} from '@automata/ui/src/DurationPickerV2/utils';
import { useFlag } from '@automata/utils/hooks';
import { SxProps } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import startCase from 'lodash/startCase';
import {
  ChangeEventHandler,
  ReactElement,
  useCallback,
  useEffect,
} from 'react';

import { ConnectorizableFileParam } from './ConnectorizableFileParam';
import { OneOfFields } from './OneOfFields';
import { SpeedInputSelect } from './SpeedInputSelect';
import { isTimeField } from './utils';
import { ONE_OF_FIELDS } from '../../../../constants';
import {
  type DriverArgument,
  parameterUnitDisplayText,
} from '../../../../data/drivers';
import { deleteNodesEdges } from '../../../../features/actions';
import { updateNodesEdgesSelection } from '../../../../features/graph/graphSlice';
import { selectDataDependencyEdges } from '../../../../features/graph/selectors';
import { selectWorkflowLevelErrorsForTaskId } from '../../../../features/workflowJsonSelectors/workflowLevelErrors';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import type {
  TaskProperties,
  TaskPropertiesForm,
  UpdateTaskParametersFn,
} from '../../../../types';
import { isStartOrStopShakeTask, validateField } from '../../../../utils';
import {
  buildModePatchForParam,
  getParamMode,
} from '../../../../utils/dataModes';
import { DataMode } from '../../../../zodSchemas/taskProperties';
import { ParameterType } from '../../../GraphEditorToolbars/ParametersToolbar/types';
import { useFieldData } from '../../hooks';
import { ExposingParameterChip } from '../ExposingParameterChip';
import { ExposingParameterPopover } from '../ExposingParameterPopover/ExposingParameterPopover';
import SlotField from '../LabwareFields/SlotField';

const sx: SxProps = {
  width: '100%',
  flex: 1,
};

const popoversMap: {
  [versionedDriverName: string]: { [parameterName: string]: ReactElement };
} = {
  hamilton_starlet_v2: {
    output_files: (
      <Box p={2}>
        <Typography variant="subtitle1">Output files</Typography>

        <Typography variant="body2">
          Output files should be named exactly as they are in Venus with the
          file extension. Multiple output files should be separated by a comma.
        </Typography>

        <Box mt={2} p={1} bgcolor="grey.100" borderRadius={1}>
          <Typography variant="overline">Example</Typography>
          <Typography variant="body2">Results.csv, Barcodes.csv</Typography>
        </Box>
      </Box>
    ),
  },
  thermo_fisher_multidrop_v2: {
    volume: (
      <Box p={2}>
        <Typography variant="subtitle1">
          Value should be entered in µl.
        </Typography>
      </Box>
    ),
  },
  brooks_xpeel_v2: {
    parameter_set: (
      <Box p={2}>
        <Typography variant="subtitle1">Parameter Set</Typography>

        <Typography variant="body2">Select a value from 0–4.</Typography>

        <Box mt={2} p={1} bgcolor="grey.100" borderRadius={1}>
          <Typography variant="overline">Example</Typography>
          <Typography variant="body2">
            0=Unspecified
            <br />
            1=Short 2.5s
            <br />
            2=Standard 5s (Default)
            <br />
            3=Extended 7.5s
            <br />
            4=Long 10s
          </Typography>
        </Box>
      </Box>
    ),
    adhere_time: (
      <Box p={2}>
        <Typography variant="subtitle1">Adhere Time</Typography>

        <Typography variant="body2">Select a value from 0–9.</Typography>

        <Box mt={2} p={1} bgcolor="grey.100" borderRadius={1}>
          <Typography variant="overline">Example</Typography>
          <Typography variant="body2">
            0=Unspecified
            <br />
            1=−2mm/Fast
            <br />
            2=−2mm/Slow
            <br />
            3=Default/Fast (Default)
            <br />
            4=Default/Slow
            <br />
            5=+2mm/Fast
            <br />
            6=+2mm/Slow
            <br />
            7=+4mm/Fast
            <br />
            8=+4mm/Slow
            <br />
            9=Custom
            <br />
          </Typography>
        </Box>
      </Box>
    ),
  },
};

// Parameter label overrides for specific driver/action/parameter combinations
const parameterLabelOverrides: {
  [versionedDriverName: string]: {
    [taskType: string]: {
      [parameterName: string]: string;
    };
  };
} = {
  thermo_fisher_multidrop_v1: {
    backflush: {
      volume: 'Volume per line',
    },
    prime: {
      volume: 'Volume per line',
    },
    shake: {
      time: 'Time (1/100 sec)',
      distance: 'Distance (mm)',
      speed: 'Speed (Hz)',
    },
  },
  thermo_fisher_multidrop_v2: {
    backflush: {
      volume: 'Volume per line',
    },
    prime: {
      volume: 'Volume per line',
    },
    shake: {
      time: 'Time (1/100 sec)',
      distance: 'Distance (mm)',
      freq: 'Freq (hertz)',
    },
  },
};

export type TaskParameterFieldProps = {
  isDefaultApplied: boolean;
  parameter: DriverArgument;
  parameterName: string;
  parameterValue: string;
  taskId: string;
  taskProperties: TaskProperties;
  versionedDriverName: string | null;
  updateTaskParameters: UpdateTaskParametersFn;
  updateTaskProperties: (formUpdate: Partial<TaskPropertiesForm>) => void;
  handleChangeSpeedUnit: (unit: 'g_force' | 'speed') => void;
  isRadiusVisible?: boolean;
};

export function TaskParameterStringArrayInput(props: {
  label: string;
  defaultValue: string;
  infoPopover?: ReactElement;
  onChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}) {
  return (
    <Stack gap={1}>
      <TextField
        label={props.label}
        sx={sx}
        defaultValue={props.defaultValue}
        infoPopover={props.infoPopover}
        infoPopoverMaxWidth={300}
        infoPopoverAnchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        infoPopoverTransformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        onChange={props.onChange}
      />
    </Stack>
  );
}

export const TaskParameterField = ({
  isDefaultApplied,
  parameter,
  parameterName,
  parameterValue,
  taskId,
  taskProperties,
  versionedDriverName,
  updateTaskParameters: updateParamViaCaster,
  updateTaskProperties,
  handleChangeSpeedUnit,
  isRadiusVisible,
}: TaskParameterFieldProps) => {
  const workflowLevelErrorsVisible = useFlag(
    'fe.canvas-builder-workflow-errors-visible'
  );
  const workflowLevelErrors = useAppSelector((state) =>
    selectWorkflowLevelErrorsForTaskId(state, taskId)
  );
  const apiBatchingEnabled = useFlag('fe.api_batching');
  const dataDependencyEdges = useAppSelector(selectDataDependencyEdges);
  const dispatch = useAppDispatch();

  const popover =
    (versionedDriverName &&
      popoversMap[versionedDriverName]?.[parameterName]) ||
    undefined;

  const label =
    (versionedDriverName &&
      parameterLabelOverrides[versionedDriverName]?.[taskProperties.taskType]?.[
        parameterName
      ]) ||
    startCase(parameterName);
  const units = parameter.units;

  const { getFieldData } = useFieldData(taskProperties);
  const {
    parameterAppliedReferenceId,
    parameterReference,
    parameterAppliedValue,
  } = getFieldData(parameterName);

  const error = workflowLevelErrorsVisible
    ? validateField(parameterAppliedValue, parameter)
    : null;

  const handleSaveValue = useCallback(
    (value: string) => {
      updateParamViaCaster(parameterName, value);

      if (parameterName === 'generate_output_file') {
        const edge = dataDependencyEdges.find((e) => e.source === taskId);

        if (edge) {
          dispatch(deleteNodesEdges({ edgeIds: [edge.id] }));
          dispatch(
            updateNodesEdgesSelection({
              nodes: { selected: [taskId], deselected: [] },
            })
          );
        }
      }
    },
    [dataDependencyEdges, dispatch, parameterName, taskId, updateParamViaCaster]
  );

  // added to save default values to store
  // without this logic, handleSaveValue method may never be called
  const hasParameter =
    taskProperties.taskParameters?.hasOwnProperty(parameterName);
  useEffect(() => {
    if (isDefaultApplied && !hasParameter) {
      handleSaveValue(parameterValue);
    }
  }, [isDefaultApplied, hasParameter, parameterValue, handleSaveValue]);

  const mode = getParamMode(taskProperties, parameterName);

  useEffect(() => {
    // set settings with 'fe.api_batching' flag off
    if (!apiBatchingEnabled && mode === DataMode.Manual) {
      updateTaskProperties(
        buildModePatchForParam(parameterName, DataMode.Connector)
      );
    }
  }, [apiBatchingEnabled, mode, parameterName, updateTaskProperties]);

  const handleDurationTime = useCallback(
    (e: number | null) => {
      const value = typeof e === 'number' ? e.toString() : '';
      handleSaveValue(value);
      updateTaskProperties({
        userTimeEstimateSeconds: value,
      });
    },
    [handleSaveValue, updateTaskProperties]
  );

  const renderField = () => {
    if (isTimeField(units)) {
      const parsedDurationSeconds = Number.parseInt(parameterAppliedValue, 10);
      const { hours, minutes, seconds } = getTimeBreakdownStrings(
        parsedDurationSeconds
      );

      // TODO(IL, 2025-02-25): Would be nice to make the form manage the state here, so that users can set H/M/S separately
      // like in the Task Property Form's Time Estimate field and in Time Constraints. A little complexity here
      // bc, since the TaskParameterField fields here are dynamic and come from the driver,
      // we'd have to support an arbitrary number of time fields in the state of this form (which other use cases don't require.)
      return (
        <Stack gap={1}>
          <Stack direction="row" alignItems="flex-end" width="100%">
            <DurationPicker
              label="Duration"
              fullWidth
              error={!!error}
              hours={hours}
              minutes={minutes}
              seconds={seconds}
              disabled={!!parameterAppliedReferenceId}
              handleUpdateTime={(timePatch) => {
                const totalSeconds = getTotalSeconds(
                  castTimeBreakdownStrings({
                    hours,
                    minutes,
                    seconds,
                    ...timePatch,
                  })
                );
                handleSaveValue(
                  totalSeconds >= 0 ? totalSeconds.toString() : ''
                );
              }}
            />
            {apiBatchingEnabled && (
              <ExposingParameterPopover
                appliedParameterId={parameterReference?.id}
                parameterName={parameterName}
                taskId={taskId}
                types={ParameterType.DURATION}
              />
            )}
          </Stack>
          {parameterAppliedReferenceId && (
            <ExposingParameterChip
              parameter={parameterReference}
              fieldName={parameterName}
              taskId={taskId}
            />
          )}
          <FieldError error={error} />
        </Stack>
      );
    }

    if (parameterName === 'slot') {
      return (
        <SlotField
          id={taskId}
          isRange={!isStartOrStopShakeTask(taskProperties.taskType)}
          onChange={handleSaveValue}
          value={parameterValue}
          errors={workflowLevelErrorsVisible ? workflowLevelErrors : []}
        />
      );
    }

    const isConnectorizableStr =
      parameterName === 'workflow_data' ||
      parameterName === 'input_csv_file' ||
      parameterName === 'plate_map_csv';

    if (!apiBatchingEnabled && isConnectorizableStr) return;

    if (isConnectorizableStr) {
      const mode =
        getParamMode(taskProperties, parameterName) ?? DataMode.Manual;

      return (
        <ConnectorizableFileParam
          inputParameterName={parameterName}
          label={label}
          mode={mode}
          required={parameter.is_required}
          taskProperties={taskProperties}
          updateTaskFields={updateTaskProperties}
        />
      );
    }

    if (parameter.type === 'str') {
      // We need to use a selector that gets a collection of fields from realDrivers.json.
      // But so far there is no such information there.
      const collection =
        Object.values(ONE_OF_FIELDS).find((fields) =>
          fields.includes(parameterName)
        ) ?? [];
      const availableFields = collection.filter(
        (field) => !!getFieldData(field).parameter
      );
      const isPartOfCollection = availableFields.length > 1;
      const firstAvailableField = availableFields[0];

      if (isPartOfCollection && parameterName !== firstAvailableField) return;
      if (isPartOfCollection && parameterName === firstAvailableField) {
        return (
          <OneOfFields
            collection={availableFields}
            taskId={taskId}
            taskProperties={taskProperties}
            updateTaskParameters={updateParamViaCaster}
          />
        );
      }

      return (
        <Stack gap={1} flex={1}>
          <Stack flexDirection="row" alignItems="flex-end" width="100%">
            <TextField
              label={label}
              error={!!error}
              sx={sx}
              value={parameterAppliedValue}
              infoPopover={popover}
              infoPopoverMaxWidth={300}
              infoPopoverAnchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              infoPopoverTransformOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              disabled={!!parameterAppliedReferenceId}
              onChange={(e) => handleSaveValue(e.target.value)}
              onBlur={(e) => handleSaveValue(e.target.value.trim())}
            />
            {apiBatchingEnabled && (
              <ExposingParameterPopover
                appliedParameterId={parameterReference?.id}
                parameterName={parameterName}
                taskId={taskId}
                types={ParameterType.STRING}
              />
            )}
          </Stack>
          {parameterAppliedReferenceId && (
            <ExposingParameterChip
              parameter={parameterReference}
              fieldName={parameterName}
              taskId={taskId}
            />
          )}
          <FieldError error={error} />
        </Stack>
      );
    }

    if (Array.isArray(parameter.type) && parameter.type[0] === 'str') {
      // Add spaces after commas for display
      const defaultValue = parameterValue.replaceAll(',', ', ');
      return (
        <TaskParameterStringArrayInput
          label={label}
          defaultValue={defaultValue}
          infoPopover={popover}
          onChange={(e) => handleSaveValue(e.target.value)}
        />
      );
    }

    if (parameter.type === 'float' || parameter.type === 'int') {
      const min = parameter.min_value ?? undefined;
      const max = parameter.max_value ?? undefined;

      // isSpinSpecialCase is a special case for the Hettich SBS300 centrifuge spin task
      const isSpinSpecialCase =
        taskProperties.taskType === 'spin' &&
        (taskProperties.instrumentId === 'hettich_sbs300_centrifuge' ||
          taskProperties.instrumentId === 'centrifuge_1');

      if (isSpinSpecialCase && parameterName === 'run_time') {
        return (
          <Stack gap={1}>
            <Stack width="100%" direction="row" alignItems="flex-end">
              <NumberField
                decimal={parameter.type === 'float'}
                label="Duration time"
                error={!!error}
                min={min}
                max={max}
                sx={sx}
                value={parameterAppliedValue}
                disabled={!!parameterAppliedReferenceId}
                onChange={handleDurationTime}
              />
              {apiBatchingEnabled && (
                <ExposingParameterPopover
                  appliedParameterId={parameterReference?.id}
                  parameterName={parameterName}
                  taskId={taskId}
                  types={
                    parameter.type === 'float'
                      ? ParameterType.FLOAT
                      : ParameterType.INTEGER
                  }
                />
              )}
            </Stack>
            {parameterAppliedReferenceId && (
              <ExposingParameterChip
                parameter={parameterReference}
                fieldName={parameterName}
                taskId={taskId}
              />
            )}
            <FieldError error={error} />
          </Stack>
        );
      }

      // Hide G Force and field for spin task
      if (isSpinSpecialCase && parameterName === 'g_force') return;
      if (
        isSpinSpecialCase &&
        !isRadiusVisible &&
        parameterName === 'radius_mm'
      )
        return;

      // Custom field for speed
      if (isSpinSpecialCase && parameterName === 'speed') {
        return (
          <>
            <SpeedInputSelect
              error={!!error}
              label={label}
              parameter={parameter}
              parameterValue={parameterValue}
              taskProperties={taskProperties}
              handleChangeSpeedUnit={handleChangeSpeedUnit}
              updateTaskParameters={updateParamViaCaster}
            />
            <FieldError error={error} />
          </>
        );
      }

      return (
        <Stack gap={1}>
          <Stack width="100%" direction="row" alignItems="flex-end">
            <NumberField
              decimal={parameter.type === 'float'}
              label={label}
              error={!!error}
              min={min}
              max={max}
              sx={sx}
              value={parameterAppliedValue}
              infoPopover={popover}
              disabled={!!parameterAppliedReferenceId}
              onChange={(e) =>
                handleSaveValue(typeof e === 'number' ? e.toString() : '')
              }
            />
            {apiBatchingEnabled && (
              <ExposingParameterPopover
                appliedParameterId={parameterReference?.id}
                parameterName={parameterName}
                taskId={taskId}
                types={
                  parameter.type === 'float'
                    ? ParameterType.FLOAT
                    : ParameterType.INTEGER
                }
              />
            )}
          </Stack>
          {parameterAppliedReferenceId && (
            <ExposingParameterChip
              parameter={parameterReference}
              fieldName={parameterName}
              taskId={taskId}
            />
          )}
          <FieldError error={error} />
        </Stack>
      );
    }

    if (parameter.type === 'bool') {
      const id = `checkbox-${parameterName}`;
      return (
        <Box>
          <Stack direction="row" alignItems="center">
            <Typography
              variant="overline"
              color="text.primary"
              component="label"
              htmlFor={id}
              sx={{ cursor: 'pointer', userSelect: 'none' }}
              py={2}
            >
              {label}
            </Typography>
            <Checkbox
              id={id}
              checked={parameterAppliedValue === 'true'}
              disabled={!!parameterAppliedReferenceId}
              onChange={(e) =>
                handleSaveValue(e.target.checked ? 'true' : 'false')
              }
            />
            {apiBatchingEnabled && (
              <ExposingParameterPopover
                appliedParameterId={parameterReference?.id}
                parameterName={parameterName}
                padding={0}
                taskId={taskId}
                types={ParameterType.BOOLEAN}
              />
            )}
          </Stack>
          {parameterAppliedReferenceId && (
            <ExposingParameterChip
              parameter={parameterReference}
              fieldName={parameterName}
              taskId={taskId}
            />
          )}
        </Box>
      );
    }
    return null;
  };

  return (
    <Stack direction="row" alignItems="center">
      <Box flex={3}>{renderField()}</Box>
      {units && (
        <Box flex={1} paddingLeft="1rem">
          {parameterUnitDisplayText(units)}
        </Box>
      )}
    </Stack>
  );
};
